import type { AccessControlProvider, CanParams, CanResponse } from '@refinedev/core';

import { getImpersonatedRental, getStoredUser, isAdminInGlobalView } from '@/services/auth';
import { isAdmin, isEmployee, isManager } from '@/types/models';

/**
 * The platform screens: data across every Rental. Only an Admin in Global view opens them.
 * An Admin impersonating a Rental works as its Manager, so these stay hidden until the
 * Impersonation ends.
 *
 * Without an accessControlProvider, <Refine> lets every <CanAccess> wrapper
 * default to can:true, so the admin-only "Rentals" nav item leaked into
 * the Sider for managers/employees (who then hit RentalList's
 * "Access Denied" panel).
 */
export const PLATFORM_RESOURCES = new Set<string>([
  'rentals',
  'admin-users',
  'global-vehicle-models',
  'security-events',
]);

// Resources the backend opens to admin and manager but not employee. Kept as
// its own set so manager is never treated as admin elsewhere.
// `users` is the user management of the Rental: employees cannot read the list.
const ADMIN_OR_MANAGER_RESOURCES = new Set<string>([
  'discounts',
  'office-hours-policies',
  'settings',
  'users',
]);

// The Rental profile singleton: every Rental role reads it (employees too, though
// they cannot open the pricing `settings`); a manager, or an Admin impersonating a
// Rental, updates it. Nobody creates or deletes it. A missing or unknown role gets nothing.
const RENTAL_PROFILE_RESOURCE = 'rental-profile';

const canAccessRentalProfile = (action: string | undefined): boolean => {
  const user = getStoredUser();
  if (!isAdmin(user) && !isManager(user) && !isEmployee(user)) return false;
  if (action === undefined || ['list', 'show', 'read'].includes(action)) return true;
  if (action === 'edit' || action === 'update') {
    return isManager(user) || (isAdmin(user) && !isAdminInGlobalView());
  }
  return false;
};

// The answer needs no request, so the Sider reads it during render: a denied menu item
// then never renders, not even as an empty row while `useCan` resolves.
export const canAccessResource = (resource: string | undefined): boolean => {
  if (resource === RENTAL_PROFILE_RESOURCE) return canAccessRentalProfile(undefined);
  if (resource && PLATFORM_RESOURCES.has(resource)) return isAdminInGlobalView();
  if (resource && ADMIN_OR_MANAGER_RESOURCES.has(resource)) {
    const user = getStoredUser();
    return isAdmin(user) || isManager(user);
  }
  return true;
};

export const canAccessAction = ({
  resource,
  action,
}: Pick<CanParams, 'resource' | 'action'>): boolean => {
  if (resource === RENTAL_PROFILE_RESOURCE) return canAccessRentalProfile(action);
  if (!canAccessResource(resource)) return false;
  if (!['create', 'edit', 'update', 'delete'].includes(action)) return true;
  const user = getStoredUser();
  if (resource === 'block-types') {
    return isManager(user) || (isAdmin(user) && !isAdminInGlobalView());
  }
  if (resource === 'vehicle-blocks' || resource === 'tasks') return !isAdminInGlobalView();
  return true;
};

// A manager, or an Admin impersonating a Rental. Employees and an Admin in Global view are not.
const isManagerOrImpersonatingAdmin = (): boolean => {
  const user = getStoredUser();
  return isManager(user) || (isAdmin(user) && getImpersonatedRental() !== null);
};

/** Who may create a Rental vehicle model from a global template. */
export const canCreateVehicleModelFromGlobal = isManagerOrImpersonatingAdmin;

/** Who may read the rental's setup status. */
export const canReadSetupStatus = isManagerOrImpersonatingAdmin;

/**
 * Who may move or copy a record to another rental: only a platform Admin, and only while
 * impersonating the rental the record is in. Managers, employees and Global view may not.
 */
export const canTransferRecords = (): boolean =>
  isAdmin(getStoredUser()) && getImpersonatedRental() !== null;

export const accessControlProvider: AccessControlProvider = {
  can: async (params: CanParams): Promise<CanResponse> => ({
    can: canAccessAction(params),
  }),
};
